import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import { useLang } from '../i18n';

const css = `
  .legal { padding: 6px 16px 32px; }
  .legal-hero { margin-bottom: 20px; padding: 16px 14px; border-radius: 12px; background: linear-gradient(135deg, rgba(129,135,246,.15), rgba(118,225,255,.06)); box-shadow: inset 0 0 0 1px rgba(118,225,255,.18); }
  .legal-hero-title { font-size: 17px; font-weight: 800; color: #fff; }
  .legal-hero-sub { font-size: 12px; color: #76e1ff; margin-top: 4px; }
  .legal-section { margin-top: 18px; }
  .legal-section-title { font-size: 13px; font-weight: 800; color: #fff; margin-bottom: 8px; letter-spacing: .2px; display: flex; align-items: center; gap: 7px; }
  .legal-section-title::before { content: ''; display: block; width: 3px; height: 14px; border-radius: 2px; background: linear-gradient(#8187f6, #76e1ff); flex: none; }
  .legal-p { font-size: 12.5px; color: #9b9ab0; line-height: 1.65; }
  .legal-p + .legal-p { margin-top: 8px; }
  .legal-ul { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
  .legal-ul li { font-size: 12.5px; color: #9b9ab0; padding-left: 14px; position: relative; line-height: 1.5; }
  .legal-ul li::before { content: '—'; position: absolute; left: 0; color: #76e1ff; }
  .legal-date { font-size: 11px; color: #5a5972; margin-top: 28px; text-align: center; }
`;

const CONTENT = {
  fr: {
    backTitle: "Conditions d'utilisation",
    heroTitle: "Conditions Générales d'Utilisation",
    heroSub: 'Dernière mise à jour : Janvier 2025',
    footer: '© 2025 Roxs Sports — Tous droits réservés',
    sections: [
      {
        title: '1. Objet',
        body: [
          'Les présentes Conditions Générales d\'Utilisation (CGU) régissent l\'accès et l\'utilisation de la plateforme de paris sportifs Roxs Sports, accessible via l\'application mobile et le site web associé.',
          'En accédant à notre service, vous acceptez sans réserve les présentes CGU. Si vous n\'acceptez pas ces conditions, vous devez cesser immédiatement d\'utiliser nos services.',
        ],
      },
      {
        title: '2. Accès au service',
        body: [
          'L\'accès à Roxs Sports est strictement réservé aux personnes majeures (18 ans et plus). En vous inscrivant, vous certifiez avoir l\'âge légal pour participer à des jeux d\'argent dans votre pays de résidence.',
          'Un seul compte est autorisé par personne. Toute tentative de création de comptes multiples entraînera la suspension immédiate de tous les comptes concernés.',
        ],
        list: [
          'Résider dans un pays où les jeux d\'argent en ligne sont légaux',
          'Fournir des informations personnelles exactes lors de l\'inscription',
          'Ne pas être inscrit sur un registre d\'exclusion aux jeux',
        ],
      },
      {
        title: '3. Inscription et compte',
        body: [
          'Lors de votre inscription, vous devez fournir des informations exactes, complètes et à jour. Roxs Sports se réserve le droit de suspendre ou clôturer tout compte contenant des informations erronées.',
          'Vous êtes responsable de la confidentialité de vos identifiants de connexion. Roxs Sports ne saurait être tenu responsable de toute utilisation non autorisée de votre compte.',
        ],
      },
      {
        title: '4. Paris et règles du jeu',
        body: [
          'Tous les paris sont définitifs une fois confirmés. Roxs Sports se réserve le droit d\'annuler ou de suspendre un pari en cas d\'erreur manifeste sur les cotes proposées.',
          'Les gains seront crédités sur votre compte dans un délai de 24 à 72 heures après la validation des résultats officiels de l\'événement concerné.',
        ],
        list: [
          'Les cotes peuvent évoluer jusqu\'au début de l\'événement',
          'Les paris en direct sont soumis à des règles spécifiques',
          'Roxs Sports se réserve le droit de limiter les mises maximales',
          'Tout pari placé après le début de l\'événement sera annulé',
        ],
      },
      {
        title: '5. Dépôts et retraits',
        body: [
          'Les dépôts sont traités immédiatement après confirmation du prestataire de paiement. Les retraits sont traités sous 3 à 5 jours ouvrés selon le mode de paiement choisi.',
          'Roxs Sports ne facture aucuns frais pour les dépôts. Des frais de traitement peuvent s\'appliquer à certains modes de retrait (voir la page Retrait pour les détails).',
        ],
      },
      {
        title: '6. Comportements interdits',
        body: ['Les comportements suivants sont strictement interdits et entraîneront la suspension immédiate du compte :'],
        list: [
          'Arbitrage de cotes ou utilisation de bots automatisés',
          'Fraude, blanchiment d\'argent ou activité illégale',
          'Collusion avec d\'autres joueurs',
          'Abus des offres promotionnelles',
          'Utilisation de plusieurs comptes',
        ],
      },
      {
        title: '7. Responsabilité',
        body: [
          'Roxs Sports fournit ses services "en l\'état" et ne peut garantir un fonctionnement ininterrompu. Roxs Sports ne saurait être tenu responsable des pertes indirectes ou consécutives résultant de l\'utilisation de ses services.',
          'Il vous appartient de vous assurer que l\'utilisation de nos services est légale dans votre juridiction.',
        ],
      },
      {
        title: '8. Modification des CGU',
        body: [
          'Roxs Sports se réserve le droit de modifier les présentes CGU à tout moment. Les modifications prendront effet dès leur publication sur la plateforme. Nous vous conseillons de consulter régulièrement ces CGU.',
        ],
      },
      {
        title: '9. Droit applicable',
        body: [
          'Les présentes CGU sont régies par le droit applicable dans le territoire où Roxs Sports est opéré. Tout litige sera soumis à la juridiction compétente correspondante.',
        ],
      },
    ],
  },
  es: {
    backTitle: 'Términos de uso',
    heroTitle: 'Términos y Condiciones de Uso',
    heroSub: 'Última actualización: Enero de 2025',
    footer: '© 2025 Roxs Sports — Todos los derechos reservados',
    sections: [
      {
        title: '1. Objeto',
        body: [
          'Los presentes Términos y Condiciones de Uso rigen el acceso y el uso de la plataforma de apuestas deportivas Roxs Sports, accesible a través de la aplicación móvil y el sitio web asociado.',
          'Al acceder a nuestro servicio, usted acepta sin reservas los presentes Términos. Si no acepta estas condiciones, debe dejar de utilizar nuestros servicios de inmediato.',
        ],
      },
      {
        title: '2. Acceso al servicio',
        body: [
          'El acceso a Roxs Sports está estrictamente reservado a personas mayores de edad (18 años o más). Al registrarse, usted certifica tener la edad legal para participar en juegos de azar en su país de residencia.',
          'Solo se permite una cuenta por persona. Cualquier intento de crear múltiples cuentas conllevará la suspensión inmediata de todas las cuentas implicadas.',
        ],
        list: [
          'Residir en un país donde los juegos de azar en línea sean legales',
          'Proporcionar información personal exacta al registrarse',
          'No estar inscrito en un registro de autoexclusión de juego',
        ],
      },
      {
        title: '3. Registro y cuenta',
        body: [
          'Al registrarse, debe proporcionar información exacta, completa y actualizada. Roxs Sports se reserva el derecho de suspender o cerrar cualquier cuenta que contenga información errónea.',
          'Usted es responsable de la confidencialidad de sus credenciales de acceso. Roxs Sports no será responsable de ningún uso no autorizado de su cuenta.',
        ],
      },
      {
        title: '4. Apuestas y reglas del juego',
        body: [
          'Todas las apuestas son definitivas una vez confirmadas. Roxs Sports se reserva el derecho de anular o suspender una apuesta en caso de error manifiesto en las cuotas ofrecidas.',
          'Las ganancias se acreditarán en su cuenta en un plazo de 24 a 72 horas tras la validación de los resultados oficiales del evento correspondiente.',
        ],
        list: [
          'Las cuotas pueden variar hasta el inicio del evento',
          'Las apuestas en directo están sujetas a reglas específicas',
          'Roxs Sports se reserva el derecho de limitar las apuestas máximas',
          'Toda apuesta realizada después del inicio del evento será anulada',
        ],
      },
      {
        title: '5. Depósitos y retiros',
        body: [
          'Los depósitos se procesan de inmediato tras la confirmación del proveedor de pago. Los retiros se procesan en un plazo de 3 a 5 días hábiles según el método de pago elegido.',
          'Roxs Sports no cobra ninguna comisión por los depósitos. Pueden aplicarse comisiones de procesamiento a ciertos métodos de retiro (consulte la página de Retiro para más detalles).',
        ],
      },
      {
        title: '6. Comportamientos prohibidos',
        body: ['Los siguientes comportamientos están estrictamente prohibidos y conllevarán la suspensión inmediata de la cuenta:'],
        list: [
          'Arbitraje de cuotas o uso de bots automatizados',
          'Fraude, blanqueo de capitales o actividad ilegal',
          'Colusión con otros jugadores',
          'Abuso de las ofertas promocionales',
          'Uso de varias cuentas',
        ],
      },
      {
        title: '7. Responsabilidad',
        body: [
          'Roxs Sports presta sus servicios "tal cual" y no puede garantizar un funcionamiento ininterrumpido. Roxs Sports no será responsable de las pérdidas indirectas o consecuentes derivadas del uso de sus servicios.',
          'Le corresponde a usted asegurarse de que el uso de nuestros servicios sea legal en su jurisdicción.',
        ],
      },
      {
        title: '8. Modificación de los Términos',
        body: [
          'Roxs Sports se reserva el derecho de modificar los presentes Términos en cualquier momento. Las modificaciones entrarán en vigor tras su publicación en la plataforma. Le recomendamos consultar estos Términos periódicamente.',
        ],
      },
      {
        title: '9. Ley aplicable',
        body: [
          'Los presentes Términos se rigen por la ley aplicable en el territorio donde opera Roxs Sports. Cualquier litigio se someterá a la jurisdicción competente correspondiente.',
        ],
      },
    ],
  },
  pt: {
    backTitle: 'Termos de uso',
    heroTitle: 'Termos e Condições de Uso',
    heroSub: 'Última atualização: Janeiro de 2025',
    footer: '© 2025 Roxs Sports — Todos os direitos reservados',
    sections: [
      {
        title: '1. Objeto',
        body: [
          'Os presentes Termos e Condições de Uso regem o acesso e a utilização da plataforma de apostas esportivas Roxs Sports, acessível pelo aplicativo móvel e pelo site associado.',
          'Ao acessar nosso serviço, você aceita sem reservas os presentes Termos. Se não aceitar estas condições, deve parar de utilizar nossos serviços imediatamente.',
        ],
      },
      {
        title: '2. Acesso ao serviço',
        body: [
          'O acesso à Roxs Sports é estritamente reservado a pessoas maiores de idade (18 anos ou mais). Ao se cadastrar, você certifica ter a idade legal para participar de jogos de azar no seu país de residência.',
          'É permitida apenas uma conta por pessoa. Qualquer tentativa de criar múltiplas contas resultará na suspensão imediata de todas as contas envolvidas.',
        ],
        list: [
          'Residir em um país onde os jogos de azar online sejam legais',
          'Fornecer informações pessoais corretas no cadastro',
          'Não estar inscrito em um registro de autoexclusão de jogos',
        ],
      },
      {
        title: '3. Cadastro e conta',
        body: [
          'No cadastro, você deve fornecer informações corretas, completas e atualizadas. A Roxs Sports reserva-se o direito de suspender ou encerrar qualquer conta que contenha informações incorretas.',
          'Você é responsável pela confidencialidade de suas credenciais de acesso. A Roxs Sports não se responsabiliza por qualquer uso não autorizado da sua conta.',
        ],
      },
      {
        title: '4. Apostas e regras do jogo',
        body: [
          'Todas as apostas são definitivas após a confirmação. A Roxs Sports reserva-se o direito de anular ou suspender uma aposta em caso de erro manifesto nas odds oferecidas.',
          'Os ganhos serão creditados na sua conta em um prazo de 24 a 72 horas após a validação dos resultados oficiais do evento em questão.',
        ],
        list: [
          'As odds podem variar até o início do evento',
          'As apostas ao vivo estão sujeitas a regras específicas',
          'A Roxs Sports reserva-se o direito de limitar as apostas máximas',
          'Toda aposta feita após o início do evento será anulada',
        ],
      },
      {
        title: '5. Depósitos e saques',
        body: [
          'Os depósitos são processados imediatamente após a confirmação do provedor de pagamento. Os saques são processados em um prazo de 3 a 5 dias úteis, conforme o método de pagamento escolhido.',
          'A Roxs Sports não cobra nenhuma taxa pelos depósitos. Podem ser aplicadas taxas de processamento a certos métodos de saque (consulte a página de Saque para mais detalhes).',
        ],
      },
      {
        title: '6. Comportamentos proibidos',
        body: ['Os seguintes comportamentos são estritamente proibidos e resultarão na suspensão imediata da conta:'],
        list: [
          'Arbitragem de odds ou uso de bots automatizados',
          'Fraude, lavagem de dinheiro ou atividade ilegal',
          'Conluio com outros jogadores',
          'Abuso das ofertas promocionais',
          'Uso de várias contas',
        ],
      },
      {
        title: '7. Responsabilidade',
        body: [
          'A Roxs Sports fornece seus serviços "no estado em que se encontram" e não pode garantir um funcionamento ininterrupto. A Roxs Sports não se responsabiliza por perdas indiretas ou consequentes decorrentes do uso de seus serviços.',
          'Cabe a você assegurar-se de que o uso de nossos serviços seja legal na sua jurisdição.',
        ],
      },
      {
        title: '8. Alteração dos Termos',
        body: [
          'A Roxs Sports reserva-se o direito de alterar os presentes Termos a qualquer momento. As alterações entrarão em vigor assim que forem publicadas na plataforma. Recomendamos consultar estes Termos regularmente.',
        ],
      },
      {
        title: '9. Lei aplicável',
        body: [
          'Os presentes Termos são regidos pela lei aplicável no território onde a Roxs Sports é operada. Qualquer litígio será submetido à jurisdição competente correspondente.',
        ],
      },
    ],
  },
  en: {
    backTitle: 'Terms of use',
    heroTitle: 'Terms and Conditions of Use',
    heroSub: 'Last updated: January 2025',
    footer: '© 2025 Roxs Sports — All rights reserved',
    sections: [
      {
        title: '1. Purpose',
        body: [
          'These Terms and Conditions of Use govern access to and use of the Roxs Sports sports betting platform, accessible via the mobile app and the associated website.',
          'By accessing our service, you accept these Terms without reservation. If you do not accept these conditions, you must immediately stop using our services.',
        ],
      },
      {
        title: '2. Access to the service',
        body: [
          'Access to Roxs Sports is strictly reserved for adults (18 years and over). By registering, you certify that you are of legal age to participate in gambling in your country of residence.',
          'Only one account is allowed per person. Any attempt to create multiple accounts will result in the immediate suspension of all accounts concerned.',
        ],
        list: [
          'Reside in a country where online gambling is legal',
          'Provide accurate personal information when registering',
          'Not be listed on a gambling self-exclusion register',
        ],
      },
      {
        title: '3. Registration and account',
        body: [
          'When registering, you must provide accurate, complete and up-to-date information. Roxs Sports reserves the right to suspend or close any account containing incorrect information.',
          'You are responsible for the confidentiality of your login credentials. Roxs Sports cannot be held liable for any unauthorised use of your account.',
        ],
      },
      {
        title: '4. Bets and game rules',
        body: [
          'All bets are final once confirmed. Roxs Sports reserves the right to cancel or suspend a bet in the event of an obvious error in the odds offered.',
          'Winnings will be credited to your account within 24 to 72 hours after validation of the official results of the relevant event.',
        ],
        list: [
          'Odds may change until the start of the event',
          'Live bets are subject to specific rules',
          'Roxs Sports reserves the right to limit maximum stakes',
          'Any bet placed after the start of the event will be cancelled',
        ],
      },
      {
        title: '5. Deposits and withdrawals',
        body: [
          'Deposits are processed immediately after confirmation by the payment provider. Withdrawals are processed within 3 to 5 business days depending on the chosen payment method.',
          'Roxs Sports does not charge any fees for deposits. Processing fees may apply to certain withdrawal methods (see the Withdrawal page for details).',
        ],
      },
      {
        title: '6. Prohibited behaviour',
        body: ['The following behaviours are strictly prohibited and will result in the immediate suspension of the account:'],
        list: [
          'Odds arbitrage or use of automated bots',
          'Fraud, money laundering or illegal activity',
          'Collusion with other players',
          'Abuse of promotional offers',
          'Use of multiple accounts',
        ],
      },
      {
        title: '7. Liability',
        body: [
          'Roxs Sports provides its services "as is" and cannot guarantee uninterrupted operation. Roxs Sports cannot be held liable for indirect or consequential losses resulting from the use of its services.',
          'It is your responsibility to ensure that the use of our services is legal in your jurisdiction.',
        ],
      },
      {
        title: '8. Amendment of the Terms',
        body: [
          'Roxs Sports reserves the right to modify these Terms at any time. Changes will take effect upon publication on the platform. We advise you to consult these Terms regularly.',
        ],
      },
      {
        title: '9. Applicable law',
        body: [
          'These Terms are governed by the law applicable in the territory where Roxs Sports is operated. Any dispute will be submitted to the corresponding competent jurisdiction.',
        ],
      },
    ],
  },
  it: {
    backTitle: 'Termini di utilizzo',
    heroTitle: 'Termini e Condizioni di Utilizzo',
    heroSub: 'Ultimo aggiornamento: gennaio 2025',
    footer: '© 2025 Roxs Sports — Tutti i diritti riservati',
    sections: [
      {
        title: '1. Oggetto',
        body: [
          'I presenti Termini e Condizioni di Utilizzo regolano l\'accesso e l\'uso della piattaforma di scommesse sportive Roxs Sports, accessibile tramite l\'app mobile e il sito web associato.',
          'Accedendo al nostro servizio, accetti senza riserve i presenti Termini. Se non accetti queste condizioni, devi smettere immediatamente di utilizzare i nostri servizi.',
        ],
      },
      {
        title: '2. Accesso al servizio',
        body: [
          'L\'accesso a Roxs Sports è riservato esclusivamente ai maggiorenni (18 anni e oltre). Registrandoti, certifichi di avere l\'età legale per partecipare al gioco d\'azzardo nel tuo paese di residenza.',
          'È consentito un solo account per persona. Qualsiasi tentativo di creare più account comporterà la sospensione immediata di tutti gli account coinvolti.',
        ],
        list: [
          'Risiedere in un paese in cui il gioco d\'azzardo online è legale',
          'Fornire informazioni personali corrette al momento della registrazione',
          'Non essere iscritto in un registro di autoesclusione dal gioco',
        ],
      },
      {
        title: '3. Registrazione e account',
        body: [
          'Al momento della registrazione, devi fornire informazioni corrette, complete e aggiornate. Roxs Sports si riserva il diritto di sospendere o chiudere qualsiasi account contenente informazioni errate.',
          'Sei responsabile della riservatezza delle tue credenziali di accesso. Roxs Sports non può essere ritenuta responsabile per qualsiasi uso non autorizzato del tuo account.',
        ],
      },
      {
        title: '4. Scommesse e regole di gioco',
        body: [
          'Tutte le scommesse sono definitive una volta confermate. Roxs Sports si riserva il diritto di annullare o sospendere una scommessa in caso di errore manifesto nelle quote offerte.',
          'Le vincite saranno accreditate sul tuo account entro 24-72 ore dopo la convalida dei risultati ufficiali dell\'evento in questione.',
        ],
        list: [
          'Le quote possono variare fino all\'inizio dell\'evento',
          'Le scommesse live sono soggette a regole specifiche',
          'Roxs Sports si riserva il diritto di limitare le puntate massime',
          'Qualsiasi scommessa piazzata dopo l\'inizio dell\'evento sarà annullata',
        ],
      },
      {
        title: '5. Depositi e prelievi',
        body: [
          'I depositi vengono elaborati immediatamente dopo la conferma del fornitore di pagamento. I prelievi vengono elaborati entro 3-5 giorni lavorativi in base al metodo di pagamento scelto.',
          'Roxs Sports non addebita alcuna commissione per i depositi. Possono essere applicate commissioni di elaborazione a determinati metodi di prelievo (consulta la pagina Prelievo per i dettagli).',
        ],
      },
      {
        title: '6. Comportamenti vietati',
        body: ['I seguenti comportamenti sono severamente vietati e comporteranno la sospensione immediata dell\'account:'],
        list: [
          'Arbitraggio di quote o uso di bot automatizzati',
          'Frode, riciclaggio di denaro o attività illegale',
          'Collusione con altri giocatori',
          'Abuso delle offerte promozionali',
          'Uso di più account',
        ],
      },
      {
        title: '7. Responsabilità',
        body: [
          'Roxs Sports fornisce i suoi servizi "così come sono" e non può garantire un funzionamento ininterrotto. Roxs Sports non può essere ritenuta responsabile per perdite indirette o consequenziali derivanti dall\'uso dei suoi servizi.',
          'È tua responsabilità assicurarti che l\'uso dei nostri servizi sia legale nella tua giurisdizione.',
        ],
      },
      {
        title: '8. Modifica dei Termini',
        body: [
          'Roxs Sports si riserva il diritto di modificare i presenti Termini in qualsiasi momento. Le modifiche avranno effetto dalla loro pubblicazione sulla piattaforma. Ti consigliamo di consultare regolarmente questi Termini.',
        ],
      },
      {
        title: '9. Legge applicabile',
        body: [
          'I presenti Termini sono regolati dalla legge applicabile nel territorio in cui opera Roxs Sports. Qualsiasi controversia sarà sottoposta alla giurisdizione competente corrispondente.',
        ],
      },
    ],
  },
  nl: {
    backTitle: 'Gebruiksvoorwaarden',
    heroTitle: 'Algemene Gebruiksvoorwaarden',
    heroSub: 'Laatst bijgewerkt: januari 2025',
    footer: '© 2025 Roxs Sports — Alle rechten voorbehouden',
    sections: [
      {
        title: '1. Doel',
        body: [
          'Deze Algemene Gebruiksvoorwaarden regelen de toegang tot en het gebruik van het Roxs Sports-sportweddenschappenplatform, toegankelijk via de mobiele app en de bijbehorende website.',
          'Door toegang te krijgen tot onze dienst aanvaardt u deze Voorwaarden zonder voorbehoud. Als u deze voorwaarden niet aanvaardt, moet u onmiddellijk stoppen met het gebruik van onze diensten.',
        ],
      },
      {
        title: '2. Toegang tot de dienst',
        body: [
          'Toegang tot Roxs Sports is strikt voorbehouden aan volwassenen (18 jaar en ouder). Door u te registreren, verklaart u de wettelijke leeftijd te hebben om deel te nemen aan kansspelen in uw woonland.',
          'Er is slechts één account per persoon toegestaan. Elke poging om meerdere accounts aan te maken leidt tot onmiddellijke schorsing van alle betrokken accounts.',
        ],
        list: [
          'Wonen in een land waar online kansspelen legaal zijn',
          'Nauwkeurige persoonlijke informatie verstrekken bij registratie',
          'Niet geregistreerd staan in een register voor zelfuitsluiting van kansspelen',
        ],
      },
      {
        title: '3. Registratie en account',
        body: [
          'Bij registratie moet u nauwkeurige, volledige en actuele informatie verstrekken. Roxs Sports behoudt zich het recht voor om elk account met onjuiste informatie te schorsen of te sluiten.',
          'U bent verantwoordelijk voor de vertrouwelijkheid van uw inloggegevens. Roxs Sports kan niet aansprakelijk worden gesteld voor onbevoegd gebruik van uw account.',
        ],
      },
      {
        title: '4. Weddenschappen en spelregels',
        body: [
          'Alle weddenschappen zijn definitief zodra ze zijn bevestigd. Roxs Sports behoudt zich het recht voor om een weddenschap te annuleren of op te schorten in geval van een kennelijke fout in de aangeboden odds.',
          'Winsten worden binnen 24 tot 72 uur na validatie van de officiële resultaten van het betreffende evenement op uw account bijgeschreven.',
        ],
        list: [
          'Odds kunnen wijzigen tot de start van het evenement',
          'Live weddenschappen zijn onderworpen aan specifieke regels',
          'Roxs Sports behoudt zich het recht voor om maximale inzetten te beperken',
          'Elke weddenschap die na de start van het evenement wordt geplaatst, wordt geannuleerd',
        ],
      },
      {
        title: '5. Stortingen en opnames',
        body: [
          'Stortingen worden onmiddellijk verwerkt na bevestiging door de betalingsprovider. Opnames worden binnen 3 tot 5 werkdagen verwerkt, afhankelijk van de gekozen betaalmethode.',
          'Roxs Sports rekent geen kosten voor stortingen. Er kunnen verwerkingskosten van toepassing zijn op bepaalde opnamemethoden (zie de pagina Opname voor details).',
        ],
      },
      {
        title: '6. Verboden gedrag',
        body: ['Het volgende gedrag is strikt verboden en leidt tot onmiddellijke schorsing van het account:'],
        list: [
          'Arbitrage van odds of gebruik van geautomatiseerde bots',
          'Fraude, witwassen van geld of illegale activiteiten',
          'Samenspanning met andere spelers',
          'Misbruik van promotionele aanbiedingen',
          'Gebruik van meerdere accounts',
        ],
      },
      {
        title: '7. Aansprakelijkheid',
        body: [
          'Roxs Sports levert zijn diensten "zoals ze zijn" en kan geen ononderbroken werking garanderen. Roxs Sports kan niet aansprakelijk worden gesteld voor indirecte of gevolgschade die voortvloeit uit het gebruik van zijn diensten.',
          'Het is uw verantwoordelijkheid om ervoor te zorgen dat het gebruik van onze diensten legaal is in uw rechtsgebied.',
        ],
      },
      {
        title: '8. Wijziging van de Voorwaarden',
        body: [
          'Roxs Sports behoudt zich het recht voor om deze Voorwaarden op elk moment te wijzigen. Wijzigingen worden van kracht zodra ze op het platform worden gepubliceerd. Wij raden u aan deze Voorwaarden regelmatig te raadplegen.',
        ],
      },
      {
        title: '9. Toepasselijk recht',
        body: [
          'Deze Voorwaarden worden beheerst door het recht dat van toepassing is in het gebied waar Roxs Sports wordt geëxploiteerd. Elk geschil wordt voorgelegd aan de bevoegde rechtbank.',
        ],
      },
    ],
  },
  tr: {
    backTitle: 'Kullanım koşulları',
    heroTitle: 'Kullanım Şartları ve Koşulları',
    heroSub: 'Son güncelleme: Ocak 2025',
    footer: '© 2025 Roxs Sports — Tüm hakları saklıdır',
    sections: [
      {
        title: '1. Amaç',
        body: [
          'İşbu Kullanım Şartları ve Koşulları, mobil uygulama ve ilgili web sitesi aracılığıyla erişilebilen Roxs Sports spor bahisleri platformuna erişimi ve kullanımını düzenler.',
          'Hizmetimize erişerek işbu Şartları çekincesiz kabul edersiniz. Bu koşulları kabul etmiyorsanız, hizmetlerimizi kullanmayı derhal bırakmalısınız.',
        ],
      },
      {
        title: '2. Hizmete erişim',
        body: [
          'Roxs Sports\'e erişim yalnızca yetişkinlere (18 yaş ve üzeri) mahsustur. Kayıt olarak, ikamet ettiğiniz ülkede kumar oynamak için yasal yaşta olduğunuzu onaylarsınız.',
          'Kişi başına yalnızca bir hesaba izin verilir. Birden fazla hesap oluşturma girişimi, ilgili tüm hesapların derhal askıya alınmasına neden olur.',
        ],
        list: [
          'Çevrimiçi kumarın yasal olduğu bir ülkede ikamet etmek',
          'Kayıt sırasında doğru kişisel bilgiler sağlamak',
          'Bir kumar kendini dışlama kaydında yer almamak',
        ],
      },
      {
        title: '3. Kayıt ve hesap',
        body: [
          'Kayıt sırasında doğru, eksiksiz ve güncel bilgiler sağlamanız gerekir. Roxs Sports, hatalı bilgi içeren herhangi bir hesabı askıya alma veya kapatma hakkını saklı tutar.',
          'Giriş bilgilerinizin gizliliğinden siz sorumlusunuz. Roxs Sports, hesabınızın izinsiz kullanımından sorumlu tutulamaz.',
        ],
      },
      {
        title: '4. Bahisler ve oyun kuralları',
        body: [
          'Tüm bahisler onaylandıktan sonra kesindir. Roxs Sports, sunulan oranlarda bariz bir hata olması durumunda bir bahsi iptal etme veya askıya alma hakkını saklı tutar.',
          'Kazançlar, ilgili etkinliğin resmi sonuçlarının doğrulanmasından sonra 24 ila 72 saat içinde hesabınıza aktarılacaktır.',
        ],
        list: [
          'Oranlar etkinliğin başlangıcına kadar değişebilir',
          'Canlı bahisler özel kurallara tabidir',
          'Roxs Sports maksimum bahisleri sınırlama hakkını saklı tutar',
          'Etkinlik başladıktan sonra yapılan her bahis iptal edilecektir',
        ],
      },
      {
        title: '5. Yatırımlar ve çekimler',
        body: [
          'Yatırımlar, ödeme sağlayıcısının onayından hemen sonra işleme alınır. Çekimler, seçilen ödeme yöntemine göre 3 ila 5 iş günü içinde işleme alınır.',
          'Roxs Sports yatırımlar için herhangi bir ücret almaz. Bazı çekim yöntemlerine işlem ücretleri uygulanabilir (ayrıntılar için Çekim sayfasına bakın).',
        ],
      },
      {
        title: '6. Yasaklanan davranışlar',
        body: ['Aşağıdaki davranışlar kesinlikle yasaktır ve hesabın derhal askıya alınmasına neden olur:'],
        list: [
          'Oran arbitrajı veya otomatik bot kullanımı',
          'Dolandırıcılık, kara para aklama veya yasa dışı faaliyet',
          'Diğer oyuncularla iş birliği',
          'Promosyon tekliflerinin kötüye kullanılması',
          'Birden fazla hesap kullanımı',
        ],
      },
      {
        title: '7. Sorumluluk',
        body: [
          'Roxs Sports hizmetlerini "olduğu gibi" sunar ve kesintisiz çalışmayı garanti edemez. Roxs Sports, hizmetlerinin kullanımından kaynaklanan dolaylı veya sonuç olarak ortaya çıkan zararlardan sorumlu tutulamaz.',
          'Hizmetlerimizin kullanımının kendi yargı bölgenizde yasal olduğundan emin olmak sizin sorumluluğunuzdadır.',
        ],
      },
      {
        title: '8. Şartlarda değişiklik',
        body: [
          'Roxs Sports, işbu Şartları herhangi bir zamanda değiştirme hakkını saklı tutar. Değişiklikler platformda yayınlandığı anda yürürlüğe girer. Bu Şartları düzenli olarak incelemenizi öneririz.',
        ],
      },
      {
        title: '9. Uygulanacak hukuk',
        body: [
          'İşbu Şartlar, Roxs Sports\'in faaliyet gösterdiği bölgede geçerli hukuka tabidir. Her türlü uyuşmazlık ilgili yetkili mahkemeye sunulacaktır.',
        ],
      },
    ],
  },
  el: {
    backTitle: 'Όροι χρήσης',
    heroTitle: 'Όροι και Προϋποθέσεις Χρήσης',
    heroSub: 'Τελευταία ενημέρωση: Ιανουάριος 2025',
    footer: '© 2025 Roxs Sports — Με επιφύλαξη παντός δικαιώματος',
    sections: [
      {
        title: '1. Σκοπός',
        body: [
          'Οι παρόντες Όροι και Προϋποθέσεις Χρήσης διέπουν την πρόσβαση και τη χρήση της πλατφόρμας αθλητικού στοιχηματισμού Roxs Sports, προσβάσιμης μέσω της εφαρμογής για κινητά και του σχετικού ιστότοπου.',
          'Με την πρόσβαση στην υπηρεσία μας, αποδέχεστε ανεπιφύλακτα τους παρόντες Όρους. Εάν δεν αποδέχεστε αυτούς τους όρους, πρέπει να σταματήσετε αμέσως τη χρήση των υπηρεσιών μας.',
        ],
      },
      {
        title: '2. Πρόσβαση στην υπηρεσία',
        body: [
          'Η πρόσβαση στη Roxs Sports προορίζεται αυστηρά για ενήλικες (18 ετών και άνω). Με την εγγραφή σας, πιστοποιείτε ότι έχετε τη νόμιμη ηλικία για να συμμετέχετε σε τυχερά παιχνίδια στη χώρα διαμονής σας.',
          'Επιτρέπεται μόνο ένας λογαριασμός ανά άτομο. Οποιαδήποτε απόπειρα δημιουργίας πολλαπλών λογαριασμών θα οδηγήσει στην άμεση αναστολή όλων των σχετικών λογαριασμών.',
        ],
        list: [
          'Να διαμένετε σε χώρα όπου τα διαδικτυακά τυχερά παιχνίδια είναι νόμιμα',
          'Να παρέχετε ακριβή προσωπικά στοιχεία κατά την εγγραφή',
          'Να μην είστε εγγεγραμμένοι σε μητρώο αυτοαποκλεισμού από τα τυχερά παιχνίδια',
        ],
      },
      {
        title: '3. Εγγραφή και λογαριασμός',
        body: [
          'Κατά την εγγραφή, πρέπει να παρέχετε ακριβή, πλήρη και επικαιροποιημένα στοιχεία. Η Roxs Sports διατηρεί το δικαίωμα να αναστείλει ή να κλείσει οποιονδήποτε λογαριασμό περιέχει εσφαλμένα στοιχεία.',
          'Είστε υπεύθυνοι για την εμπιστευτικότητα των διαπιστευτηρίων σύνδεσής σας. Η Roxs Sports δεν φέρει ευθύνη για οποιαδήποτε μη εξουσιοδοτημένη χρήση του λογαριασμού σας.',
        ],
      },
      {
        title: '4. Στοιχήματα και κανόνες παιχνιδιού',
        body: [
          'Όλα τα στοιχήματα είναι οριστικά μόλις επιβεβαιωθούν. Η Roxs Sports διατηρεί το δικαίωμα να ακυρώσει ή να αναστείλει ένα στοίχημα σε περίπτωση προφανούς σφάλματος στις προσφερόμενες αποδόσεις.',
          'Τα κέρδη θα πιστωθούν στον λογαριασμό σας εντός 24 έως 72 ωρών μετά την επικύρωση των επίσημων αποτελεσμάτων του σχετικού γεγονότος.',
        ],
        list: [
          'Οι αποδόσεις ενδέχεται να μεταβληθούν μέχρι την έναρξη του γεγονότος',
          'Τα ζωντανά στοιχήματα υπόκεινται σε ειδικούς κανόνες',
          'Η Roxs Sports διατηρεί το δικαίωμα να περιορίσει τα μέγιστα ποντάρισμα',
          'Κάθε στοίχημα που τοποθετείται μετά την έναρξη του γεγονότος θα ακυρώνεται',
        ],
      },
      {
        title: '5. Καταθέσεις και αναλήψεις',
        body: [
          'Οι καταθέσεις επεξεργάζονται αμέσως μετά την επιβεβαίωση από τον πάροχο πληρωμών. Οι αναλήψεις επεξεργάζονται εντός 3 έως 5 εργάσιμων ημερών ανάλογα με τη μέθοδο πληρωμής.',
          'Η Roxs Sports δεν χρεώνει καμία προμήθεια για τις καταθέσεις. Ενδέχεται να ισχύουν προμήθειες επεξεργασίας σε ορισμένες μεθόδους ανάληψης (δείτε τη σελίδα Ανάληψης για λεπτομέρειες).',
        ],
      },
      {
        title: '6. Απαγορευμένες συμπεριφορές',
        body: ['Οι ακόλουθες συμπεριφορές απαγορεύονται αυστηρά και θα οδηγήσουν στην άμεση αναστολή του λογαριασμού:'],
        list: [
          'Arbitrage αποδόσεων ή χρήση αυτοματοποιημένων bots',
          'Απάτη, ξέπλυμα χρήματος ή παράνομη δραστηριότητα',
          'Συμπαιγνία με άλλους παίκτες',
          'Κατάχρηση των προσφορών',
          'Χρήση πολλαπλών λογαριασμών',
        ],
      },
      {
        title: '7. Ευθύνη',
        body: [
          'Η Roxs Sports παρέχει τις υπηρεσίες της "ως έχουν" και δεν μπορεί να εγγυηθεί αδιάλειπτη λειτουργία. Η Roxs Sports δεν φέρει ευθύνη για έμμεσες ή παρεπόμενες ζημίες που προκύπτουν από τη χρήση των υπηρεσιών της.',
          'Είναι δική σας ευθύνη να διασφαλίσετε ότι η χρήση των υπηρεσιών μας είναι νόμιμη στη δικαιοδοσία σας.',
        ],
      },
      {
        title: '8. Τροποποίηση των Όρων',
        body: [
          'Η Roxs Sports διατηρεί το δικαίωμα να τροποποιήσει τους παρόντες Όρους ανά πάσα στιγμή. Οι αλλαγές τίθενται σε ισχύ μόλις δημοσιευτούν στην πλατφόρμα. Σας συνιστούμε να συμβουλεύεστε τακτικά αυτούς τους Όρους.',
        ],
      },
      {
        title: '9. Εφαρμοστέο δίκαιο',
        body: [
          'Οι παρόντες Όροι διέπονται από το δίκαιο που ισχύει στην επικράτεια όπου λειτουργεί η Roxs Sports. Κάθε διαφορά θα υποβάλλεται στην αντίστοιχη αρμόδια δικαιοδοσία.',
        ],
      },
    ],
  },
  ro: {
    backTitle: 'Termeni de utilizare',
    heroTitle: 'Termeni și Condiții de Utilizare',
    heroSub: 'Ultima actualizare: ianuarie 2025',
    footer: '© 2025 Roxs Sports — Toate drepturile rezervate',
    sections: [
      {
        title: '1. Obiect',
        body: [
          'Prezenții Termeni și Condiții de Utilizare reglementează accesul și utilizarea platformei de pariuri sportive Roxs Sports, accesibilă prin aplicația mobilă și site-ul web asociat.',
          'Prin accesarea serviciului nostru, acceptați fără rezerve prezenții Termeni. Dacă nu acceptați aceste condiții, trebuie să încetați imediat utilizarea serviciilor noastre.',
        ],
      },
      {
        title: '2. Accesul la serviciu',
        body: [
          'Accesul la Roxs Sports este strict rezervat persoanelor majore (18 ani și peste). Prin înregistrare, certificați că aveți vârsta legală pentru a participa la jocuri de noroc în țara dvs. de reședință.',
          'Este permis un singur cont de persoană. Orice tentativă de a crea conturi multiple va duce la suspendarea imediată a tuturor conturilor implicate.',
        ],
        list: [
          'Să locuiți într-o țară în care jocurile de noroc online sunt legale',
          'Să furnizați informații personale corecte la înregistrare',
          'Să nu fiți înscris într-un registru de autoexcludere de la jocuri',
        ],
      },
      {
        title: '3. Înregistrare și cont',
        body: [
          'La înregistrare, trebuie să furnizați informații corecte, complete și actualizate. Roxs Sports își rezervă dreptul de a suspenda sau închide orice cont care conține informații eronate.',
          'Sunteți responsabil pentru confidențialitatea datelor dvs. de autentificare. Roxs Sports nu poate fi trasă la răspundere pentru orice utilizare neautorizată a contului dvs.',
        ],
      },
      {
        title: '4. Pariuri și reguli de joc',
        body: [
          'Toate pariurile sunt definitive odată confirmate. Roxs Sports își rezervă dreptul de a anula sau suspenda un pariu în cazul unei erori evidente în cotele oferite.',
          'Câștigurile vor fi creditate în contul dvs. în termen de 24 până la 72 de ore după validarea rezultatelor oficiale ale evenimentului respectiv.',
        ],
        list: [
          'Cotele se pot modifica până la începutul evenimentului',
          'Pariurile live sunt supuse unor reguli specifice',
          'Roxs Sports își rezervă dreptul de a limita mizele maxime',
          'Orice pariu plasat după începerea evenimentului va fi anulat',
        ],
      },
      {
        title: '5. Depuneri și retrageri',
        body: [
          'Depunerile sunt procesate imediat după confirmarea de către furnizorul de plăți. Retragerile sunt procesate în termen de 3 până la 5 zile lucrătoare, în funcție de metoda de plată aleasă.',
          'Roxs Sports nu percepe niciun comision pentru depuneri. Se pot aplica comisioane de procesare anumitor metode de retragere (consultați pagina Retragere pentru detalii).',
        ],
      },
      {
        title: '6. Comportamente interzise',
        body: ['Următoarele comportamente sunt strict interzise și vor duce la suspendarea imediată a contului:'],
        list: [
          'Arbitraj de cote sau utilizarea de boți automatizați',
          'Fraudă, spălare de bani sau activitate ilegală',
          'Coluziune cu alți jucători',
          'Abuzul de ofertele promoționale',
          'Utilizarea mai multor conturi',
        ],
      },
      {
        title: '7. Răspundere',
        body: [
          'Roxs Sports își furnizează serviciile "ca atare" și nu poate garanta o funcționare neîntreruptă. Roxs Sports nu poate fi trasă la răspundere pentru pierderi indirecte sau subsecvente rezultate din utilizarea serviciilor sale.',
          'Este responsabilitatea dvs. să vă asigurați că utilizarea serviciilor noastre este legală în jurisdicția dvs.',
        ],
      },
      {
        title: '8. Modificarea Termenilor',
        body: [
          'Roxs Sports își rezervă dreptul de a modifica prezenții Termeni în orice moment. Modificările vor intra în vigoare de îndată ce sunt publicate pe platformă. Vă recomandăm să consultați periodic acești Termeni.',
        ],
      },
      {
        title: '9. Legea aplicabilă',
        body: [
          'Prezenții Termeni sunt guvernați de legea aplicabilă pe teritoriul în care este operată Roxs Sports. Orice litigiu va fi supus jurisdicției competente corespunzătoare.',
        ],
      },
    ],
  },
};

export default function Terms() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={c.backTitle} />
      <div className="legal">
        <div className="legal-hero">
          <div className="legal-hero-title">{c.heroTitle}</div>
          <div className="legal-hero-sub">{c.heroSub}</div>
        </div>

        {c.sections.map((s) => (
          <div className="legal-section" key={s.title}>
            <div className="legal-section-title">{s.title}</div>
            {s.body.map((p, i) => <p className="legal-p" key={i}>{p}</p>)}
            {s.list && (
              <ul className="legal-ul">
                {s.list.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            )}
          </div>
        ))}

        <p className="legal-date">{c.footer}</p>
      </div>
    </PhoneShell>
  );
}
